import { Switch as SwitchPrimitive } from "@base-ui/react/switch";
import { cva } from "class-variance-authority";
import { useId } from "react";
import { cn } from "@/lib/utils";

const switchVariants = cva(
  ["inline-flex", "items-start", "gap-(--spacing-8)"],
  {
    variants: {
      disabled: {
        true: ["opacity-[0.54]", "cursor-not-allowed"],
        false: ["cursor-pointer"],
      },
    },
    defaultVariants: { disabled: false },
  },
);

const trackStyles = cva([
  "relative",
  "shrink-0",
  "w-[44px]",
  "h-(--spacing-24)",
  "rounded-full",
  "bg-surface-container-highest",
  "data-[checked]:bg-primary",
  "transition-colors",
  "duration-200",
  "ease-in-out",
  "motion-reduce:transition-none",
  "focus-visible:outline-none",
  "focus-visible:ring-2",
  "focus-visible:ring-offset-2",
  "focus-visible:ring-tertiary-tertiary",
  "cursor-[inherit]",
  "[@media(pointer:coarse)]:before:content-['']",
  "[@media(pointer:coarse)]:before:absolute",
  "[@media(pointer:coarse)]:before:inset-x-0",
  "[@media(pointer:coarse)]:before:inset-y-[-12px]",
]);

const thumbStyles = cva([
  "absolute",
  "top-(--spacing-2)",
  "w-(--spacing-20)",
  "h-(--spacing-20)",
  "rounded-full",
  "bg-surface-container-lowest",
  "shadow-elevation-1",
  "translate-x-(--spacing-2)",
  "data-[checked]:translate-x-[22px]",
  "transition-transform",
  "duration-150",
  "ease-in-out",
  "motion-reduce:transition-none",
]);

const labelTextStyles = cva([
  "text-text-default",
  "font-(family-name:--font-family-body)",
  "font-(number:--font-weight-medium)",
  "text-(length:--font-size-body-medium)",
  "leading-(--font-line-height-20)",
  "tracking-(--font-letter-spacing-default)",
]);

const descriptionStyles = cva([
  "text-text-subtle",
  "font-(family-name:--font-family-body)",
  "font-(number:--font-weight-regular)",
  "text-(length:--font-size-body-small)",
  "leading-(--font-line-height-16)",
]);

export type SwitchProps = Omit<
  SwitchPrimitive.Root.Props,
  "className" | "aria-label" | "aria-describedby"
> & { description?: string } & (
    | { label: string; "aria-label"?: never }
    | { label?: never; "aria-label": string }
  );

/**
 * Switch control for binary settings that take effect immediately.
 *
 * @example
 * <Switch aria-label="Enable notifications" />
 * <Switch label="Enable notifications" />
 * <Switch label="Enable notifications" description="Receive alerts for new events" />
 */
function Switch({
  label,
  description,
  disabled = false,
  id,
  ...props
}: SwitchProps) {
  const generatedId = useId();
  const switchId = id ?? generatedId;

  // <label> makes clicking the label text activate the switch; aria-labelledby is a fallback for screen readers that ignore the <label>→<button> association.
  return (
    <label htmlFor={switchId} className={cn(switchVariants({ disabled }))}>
      <SwitchPrimitive.Root
        {...props}
        id={switchId}
        disabled={disabled}
        aria-labelledby={label ? `${switchId}-label` : undefined}
        aria-describedby={description ? `${switchId}-description` : undefined}
        className={trackStyles()}
      >
        <SwitchPrimitive.Thumb className={thumbStyles()} />
      </SwitchPrimitive.Root>

      {(label || description) && (
        <div className="flex flex-col gap-(--spacing-4)">
          {label && (
            <span id={`${switchId}-label`} className={labelTextStyles()}>
              {label}
            </span>
          )}
          {description && (
            <span
              id={`${switchId}-description`}
              className={descriptionStyles()}
            >
              {description}
            </span>
          )}
        </div>
      )}
    </label>
  );
}

export { Switch };
